<template>
  <component :is="tag" data-component-name="Cards.Aces">
    <CountryFlag v-if="country_code" :country="country_code" />

    <div class="top">
      <div class="left">
        <AppAvatar
          :src="avatar_url"
          :alt="`${full_name} ${$t('photo')}`"
          :initials="getInitials(full_name)"
          size="8rem"
        />
      </div>

      <div class="right">
        <b>{{ full_name }}</b>

        <span class="country">
          {{ country }}
        </span>
      </div>
    </div>

    <div
      v-if="bio_content"
      v-html="bio_content"
      :class="['bio', { expanded }]"
    />

    <Buttons.ReadMore @click="toggleExpanded">
      {{ expanded ? $t('Hide') : $t('Read_More') }}
    </Buttons.ReadMore>

    <div v-if="social_links.length" class="links">
      <NuxtLink
        v-for="{ type, url } of social_links"
        :key="url"
        :to="url"
        target="_blank"
      >
        <SvgIcon v-if="type === 'linkedin'" name="linked-in" />
        <SvgIcon v-if="type === 'website'" name="planet" />
        <SvgIcon v-if="type === 'youtube'" name="youtube" />
        <SvgIcon v-if="type === 'github'" name="github" />
        <SvgIcon v-if="type === 'twitter'" name="x" />
        <SvgIcon v-if="type === 'facebook'" name="facebook" />
      </NuxtLink>
    </div>
  </component>
</template>

<script setup lang="ts">
// components
import CountryFlag from 'vue-country-flag-next';
import Buttons from '~/components/redesign/Buttons';
import AppAvatar from '~/components/redesign/AppAvatar.vue';

// utils
import getInitials from '~/utils/getInitials';

export type LinkType =
  | 'linkedin'
  | 'website'
  | 'youtube'
  | 'github'
  | 'twitter'
  | 'facebook';

export interface Link {
  type: LinkType;
  url: string;
}

export interface CardsAcesProps {
  avatar_url: string;
  full_name: string;
  country: string;
  country_code: string;
  bio_content: string;
  social_links: Link[];
}

withDefaults(
  defineProps<{ tag: keyof HTMLElementTagNameMap } & CardsAcesProps>(),
  {
    tag: 'div',
    links: () => [],
  },
);

const expanded = ref(false);
const toggleExpanded = () => {
  expanded.value = !expanded.value;
};
</script>

<style scoped lang="scss">
@use '_/mixins/flex';
@use '_/mixins/media';
@use '_/mixins/typo';
@use '_/mixins/text-transform';
@use '_/mixins/component';
@use '_/mixins/size';
@use '_/variables/shadow';
@use '_/fn';

[data-component-name='Cards.Aces'] {
  position: relative;
  padding: 2rem 1.5rem;
  box-shadow: shadow.$elevation-1;

  &:not(:last-child) {
    margin-bottom: 2rem;
  }

  .flag {
    position: absolute;
    top: 1.5rem;
    right: 2rem;
  }

  .top {
    @include flex.center-start;

    gap: 0 1.83rem;
    margin-bottom: 1.5rem;

    @include media.mobile {
      @include flex.start-start;

      flex-direction: column;
      gap: 1rem 0;
    }

    .right {
      @include flex.start-start;

      flex-direction: column;
      gap: 0.25rem 0;

      b {
        @include typo.subtitle-1;
      }

      span.country {
        @include typo.body-3;
      }
    }
  }

  &:deep(.bio) {
    margin-bottom: 0.5rem;

    @include text-transform.truncate-to-rows(2);

    &.expanded {
      @include text-transform.truncate-to-rows(100);
    }

    p {
      @include typo.body-2;

      line-height: 1.7rem !important;

      &:not(:last-child) {
        margin-bottom: 0.5rem;
      }

      a {
        @include component.app-link;

        font-size: inherit;
        line-height: inherit;
      }
    }
  }

  .links {
    margin-top: 1.5rem;

    @include flex.center-start;

    gap: 0 1rem;

    a {
      display: flex;

      svg {
        @include size.fixed(2rem);

        fill: fn.token('surf-cont-secon-action');
      }
    }
  }
}
</style>
